import {
  useHtzAirHandlingUnitListQuery,
  useHtzAirHandlingUnitShowQuery,
  useLazyHtzAirHandlingUnitListQuery,
} from "@/services/backend/htz/ahu/api/ahu-api";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGet,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useAirHandlingUnitOffline(workOrderId: UUID, customerId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzAirHandlingUnitListQuery();

  const prepMsg = t("Speichere RLT-Anlagen (Stammdaten) ...");
  const doneMsg = t("RLT-Anlagen (Stammdaten)");

  const prepare = async () => {
    const result = await load({
      customer: { active: true, values: [customerId] },
    });
    if (result.error) {
      throw result.error;
    }

    addStale("airHandlingUnit");
    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      result.data!.items.map(async (item) => {
        await documentDbPut<AirHandlingUnit>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "airHandlingUnit",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetAirHandlingUnit(id: string): Query<AirHandlingUnit> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzAirHandlingUnitShowQuery({ id });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<AirHandlingUnit>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airHandlingUnit",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useListAirHandlingUnit(
  workOrderId: string,
  customerId: string,
): Query<AirHandlingUnit[]> {
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzAirHandlingUnitListQuery({
    customer: {
      active: true,
      values: [customerId],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<AirHandlingUnit>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "airHandlingUnit",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.items,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
